import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DocumentStatus } from '@/types';

type SortField = 'fileName' | 'status' | 'fileSizeBytes' | 'pageCount' | 'createdAt';
type SortOrder = 'asc' | 'desc';

interface DocumentFilters {
  page: number;
  statusFilter: DocumentStatus | '';
  selectedLabelIds: string[];
  sortBy: SortField | '';
  sortOrder: SortOrder;
}

export default function useDocumentFilters(pageSize: number) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const statusFilter = (searchParams.get('status') ?? '') as DocumentStatus | '';
  const labelIdsParam = searchParams.get('labelIds');
  const selectedLabelIds = useMemo(
    () => labelIdsParam?.split(',').filter(Boolean) ?? [],
    [labelIdsParam],
  );
  const sortBy = (searchParams.get('sortBy') ?? '') as SortField | '';
  const sortOrder = (searchParams.get('sortOrder') ?? 'desc') as SortOrder;

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (p: number | ((prev: number) => number)) => {
      const newPage = typeof p === 'function' ? p(page) : p;
      updateParams({ page: newPage === 1 ? undefined : String(newPage) });
    },
    [page, updateParams],
  );

  const setStatusFilter = useCallback(
    (status: string) => {
      updateParams({ status: status || undefined, page: undefined });
    },
    [updateParams],
  );

  const toggleLabel = useCallback(
    (labelId: string) => {
      const newIds = selectedLabelIds.includes(labelId)
        ? selectedLabelIds.filter((id) => id !== labelId)
        : [...selectedLabelIds, labelId];
      updateParams({
        labelIds: newIds.length > 0 ? newIds.join(',') : undefined,
        page: undefined,
      });
    },
    [selectedLabelIds, updateParams],
  );

  const clearLabelFilters = useCallback(() => {
    updateParams({ labelIds: undefined, page: undefined });
  }, [updateParams]);

  const handleSortChange = useCallback(
    (sort: { sortBy: SortField; sortOrder: SortOrder }) => {
      updateParams({ sortBy: sort.sortBy, sortOrder: sort.sortOrder, page: undefined });
    },
    [updateParams],
  );

  const filters: DocumentFilters = {
    page,
    statusFilter,
    selectedLabelIds,
    sortBy,
    sortOrder,
  };

  const queryArgs = {
    page,
    pageSize,
    status: statusFilter || undefined,
    labelIds: selectedLabelIds.length > 0 ? selectedLabelIds.join(',') : undefined,
    sortBy: sortBy || undefined,
    sortOrder: sortBy ? sortOrder : undefined,
  };

  return {
    filters,
    queryArgs,
    setPage,
    setStatusFilter,
    toggleLabel,
    clearLabelFilters,
    handleSortChange,
  };
}
